/*
 * CourseNearbyMore — pull past the end of a nearby list to search further out.
 *
 * A nearby list opens at 80km, which is the right first answer almost
 * everywhere and the wrong one for anybody who lives somewhere with no golf in
 * it. The alternative to this was a radius control, which asks a customer who
 * came here to avoid typing a club name to first pick a number in kilometres.
 * Reaching the end of the list is already the moment they know it didn't
 * answer, so that is where the offer belongs — and pulling past the end is what
 * a phone user does at that moment anyway.
 *
 * Three ways in, all landing on the same button: pull past the end with a
 * finger, keep scrolling past it with a wheel, or click/Enter it. The gesture
 * is the discoverable one; the button is the reason the gesture isn't the only
 * one, because a gesture nobody can tab to is not a control.
 *
 * Shared rather than duplicated into course-designer.js and
 * hero-course-search.js — those two hand-copy their small formatting helpers
 * from each other, which is fine for six lines and not for a gesture. Both
 * components style their own footer; this owns only the behaviour, and takes a
 * class prefix so each keeps its own BEM namespace.
 */
(function () {
  'use strict';

  if (window.CourseNearbyMore) return;

  // How far past the end the list has to travel before it widens. Roughly a
  // thumb's pull: short enough to find by accident, long enough that the
  // momentum tail of an ordinary flick doesn't fire it.
  var PULL_THRESHOLD = 72;
  // A wheel notch is ~100px of delta and a trackpad's are far smaller, so the
  // wheel is scaled to make a comparable amount of gesture out of it.
  var WHEEL_SCALE = 0.45;
  // A wheel gesture is over when the events stop arriving.
  var WHEEL_IDLE_MS = 160;

  class CourseNearbyMore {
    /*
     * scroller — the element that scrolls (the <ul> itself, in both components).
     * prefix   — BEM block name, so the footer takes the host's own classes.
     * labels   — { more, loading, end }.
     * onExpand — called when the pull completes. Fetches the next step and must
     *            call done(exhausted) exactly once.
     */
    constructor(options) {
      this.scroller = options.scroller;
      this.prefix = options.prefix;
      this.labels = options.labels;
      this.onExpand = options.onExpand;

      this._enabled = false;
      this._loading = false;
      this._exhausted = false;
      this._pull = 0;
      this._touchY = null;
      this._wheelIdle = null;

      this.build();
      this.listen();
    }

    build() {
      // role="presentation" so the listbox above it stays a list of courses:
      // this is a control that acts on the list, not a course in it. Every
      // query for a row in both components names the suggestion class rather
      // than `li`, so the arrow keys pass over it too.
      this.el = document.createElement('li');
      this.el.className = this.prefix + '__more';
      this.el.setAttribute('role', 'presentation');
      this.el.hidden = true;

      this.buttonEl = document.createElement('button');
      this.buttonEl.type = 'button';
      this.buttonEl.className = this.prefix + '__more-button';

      // A down arrow above the words, centred — the direction of the gesture,
      // stated once at the top of the tab. Above rather than beside: alongside
      // the text it read as a bullet on a line of copy, and what it is actually
      // labelling is the pull itself, which comes from up here and goes down.
      var arrow = document.createElement('span');
      arrow.className = this.prefix + '__more-arrow';
      arrow.setAttribute('aria-hidden', 'true');
      this.buttonEl.appendChild(arrow);

      this.labelEl = document.createElement('span');
      this.labelEl.className = this.prefix + '__more-label';
      this.labelEl.textContent = this.labels.more;
      this.buttonEl.appendChild(this.labelEl);

      this.buttonEl.addEventListener('click', this.fire.bind(this));
      this.el.appendChild(this.buttonEl);
    }

    listen() {
      this.scroller.addEventListener('scroll', this.onScroll.bind(this));
      // Not passive: both of these have to be able to stop the gesture
      // chaining out of the list — into the page behind a desktop dropdown, or
      // into the sheet behind a phone list.
      this.scroller.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
      this.scroller.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
      this.scroller.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
      this.scroller.addEventListener('touchend', this.release.bind(this));
      this.scroller.addEventListener('touchcancel', this.release.bind(this));
    }

    // Called at the end of every nearby render, because the list is rebuilt
    // from scratch each time and this has to be its last child again.
    mount(listEl) {
      listEl.appendChild(this.el);
      this.setPull(0);
      this.syncEnd();
    }

    // A typed search is not a radius and has nothing to widen, so this is off
    // for every list but a nearby one.
    setEnabled(on) {
      this._enabled = !!on;
      this._exhausted = false;
      this._loading = false;
      this.el.hidden = !on;
      this.el.classList.remove('is-exhausted', 'is-loading');
      this.labelEl.textContent = this.labels.more;
      this.buttonEl.disabled = false;
      this.setPull(0);
    }

    // Nothing new came back, so there is nothing left to offer *out here*. Said
    // rather than hidden — a control that vanishes at the moment it is used
    // reads as a fault — and the line has to be careful not to sound like the
    // end of the catalogue, which is a different and much worse claim. The
    // customer's course may well be three counties away; widening the circle
    // is simply the wrong tool for finding it, and the field above is the
    // right one.
    setExhausted() {
      this._exhausted = true;
      this._loading = false;
      this.el.classList.remove('is-loading');
      this.el.classList.add('is-exhausted');
      this.labelEl.textContent = this.labels.end;
      this.buttonEl.disabled = true;
      this.setPull(0);
    }

    // Whether the list is scrolled to the end of its COURSES — which is not the
    // end of its content, because this footer is content too. Measuring the
    // whole thing oscillates: reaching the end reveals the footer, the footer
    // makes the list taller, the list is no longer at its end, the footer
    // collapses, the list is at its end again. Discounting our own height is
    // what breaks that loop, and it is also the more honest question: the offer
    // belongs where the customer has seen every course, not where they have
    // scrolled past the offer itself.
    //
    // A list too short to scroll is trivially at its end, which is right — that
    // is the emptiest answer of all and the one most in need of this.
    atEnd() {
      return this.scroller.scrollTop + this.scroller.clientHeight >=
        this.scroller.scrollHeight - (this.el.offsetHeight || 0) - 1;
    }

    onScroll() {
      if (this._pull > 0) return; // a pull is already holding it open
      this.syncEnd();
    }

    syncEnd() {
      if (!this._enabled) return;
      this.el.classList.toggle('is-at-end', this.atEnd());
    }

    active() {
      return this._enabled && !this._loading && !this._exhausted;
    }

    onWheel(e) {
      if (!this.active()) return;
      // Scrolling up, or still with list left to scroll: an ordinary scroll.
      if (e.deltaY <= 0) {
        if (this._pull > 0) {
          e.preventDefault();
          this.addPull(e.deltaY * WHEEL_SCALE);
        }
        return;
      }
      if (!this.atEnd()) return;
      e.preventDefault();
      this.addPull(e.deltaY * WHEEL_SCALE);
      // A wheel has no "up" event, so the end of the gesture is inferred from
      // the events stopping. Without this the pull would stay open until the
      // next scroll of any kind.
      clearTimeout(this._wheelIdle);
      this._wheelIdle = setTimeout(this.release.bind(this), WHEEL_IDLE_MS);
    }

    onTouchStart(e) {
      this._touchY = e.touches.length ? e.touches[0].clientY : null;
    }

    onTouchMove(e) {
      if (!this.active() || this._touchY === null || !e.touches.length) return;
      var y = e.touches[0].clientY;
      var dy = this._touchY - y; // finger up = list moving down = positive
      this._touchY = y;
      if (dy > 0 && !this.atEnd()) return; // still list left to scroll
      if (dy < 0 && this._pull <= 0) return; // scrolling back up the list
      e.preventDefault();
      this.addPull(dy);
    }

    addPull(px) {
      this.setPull(Math.max(0, Math.min(PULL_THRESHOLD, this._pull + px)), true);
      if (this._pull >= PULL_THRESHOLD) this.fire();
    }

    // Progress reaches the CSS as a unitless 0..1 custom property, so the
    // footer's height is one declaration there rather than a stream of inline
    // styles written from here.
    setPull(px, pulling) {
      this._pull = px;
      this.el.classList.toggle('is-pulling', !!pulling);
      this.el.style.setProperty('--pull', String(px / PULL_THRESHOLD));
      if (px > 0) {
        this.el.classList.add('is-at-end');
        // The list is height-capped in both components, so a footer that grows
        // grows the scrollable content rather than the box. Following it down
        // is what keeps the thing being pulled in view while it is pulled.
        this.scroller.scrollTop = this.scroller.scrollHeight;
      }
    }

    release() {
      clearTimeout(this._wheelIdle);
      this._touchY = null;
      if (this._pull > 0) this.setPull(0);
      this.syncEnd();
    }

    fire() {
      if (!this.active()) return;
      this._loading = true;
      this.setPull(0);
      this.el.classList.add('is-loading');
      this.el.classList.add('is-at-end');
      this.labelEl.textContent = this.labels.loading;
      this.buttonEl.disabled = true;
      this.onExpand(this.done.bind(this));
    }

    done(exhausted) {
      this._loading = false;
      this.el.classList.remove('is-loading');
      if (exhausted) {
        this.setExhausted();
        return;
      }
      this.labelEl.textContent = this.labels.more;
      this.buttonEl.disabled = false;
      this.syncEnd();
    }
  }

  window.CourseNearbyMore = CourseNearbyMore;
})();
